import {
  ScenarioCardTitle,
  ScenarioDetailsValue,
  ScenarioItem,
  ScenarioItemLabel,
  ScenarioLabel,
  ScenarioList,
  ScenarioSayList,
  ScenarioSayValue,
  ScenarioSection,
  ScenarioCard as StyledScenarioCard,
} from '../Scenario.styled';

interface ScenarioDetail {
  key: string;
  value: string;
}

interface ScenarioCardProps {
  scenario: {
    title: string;
    say: string;
    // Support both formats: key-value objects, string arrays, or a single string
    details: ScenarioDetail[] | readonly string[] | string;
  };
  index: number;
}

// Helper to normalize details into a consistent format
function normalizeDetails(
  details: ScenarioDetail[] | readonly string[] | string,
): ScenarioDetail[] {
  if (details === null) {
    return [];
  }

  if (typeof details === 'string') {
    return [{ key: '', value: details }];
  }

  if (details.length === 0) {
    return [];
  }

  // Check if it's already in {key, value} format
  if (typeof details[0] === 'object' && 'key' in details[0]) {
    return details as ScenarioDetail[];
  }

  // Parse "key: value" strings
  return (details as readonly string[]).map(detail => {
    const separatorIndex = detail.indexOf(': ');
    if (separatorIndex !== -1) {
      return {
        key: detail.substring(0, separatorIndex).trim(),
        value: detail.substring(separatorIndex + 2).trim(),
      };
    }
    return { key: detail.trim(), value: '—' };
  });
}

export const ScenarioCard = ({ scenario, index }: ScenarioCardProps) => {
  const normalizedDetails = normalizeDetails(scenario.details);

  return (
    <StyledScenarioCard data-test-id={`agent-wizard-scenario-card-${index}`}>
      <ScenarioCardTitle>
        Scenario {index + 1}: {scenario.title}
      </ScenarioCardTitle>

      <ScenarioSection>
        <ScenarioLabel>Say:</ScenarioLabel>
        <ScenarioSayList>
          <ScenarioItem>
            <ScenarioSayValue>{scenario.say}</ScenarioSayValue>
          </ScenarioItem>
        </ScenarioSayList>
      </ScenarioSection>

      <ScenarioSection>
        <ScenarioLabel>Details:</ScenarioLabel>
        <ScenarioList>
          {normalizedDetails.map((detail, detailIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: existing
            <ScenarioItem key={detailIndex}>
              {detail.key ? (
                <ScenarioItemLabel>{detail.key}:</ScenarioItemLabel>
              ) : (
                <ScenarioItemLabel />
              )}
              <ScenarioDetailsValue>{detail.value}</ScenarioDetailsValue>
            </ScenarioItem>
          ))}
        </ScenarioList>
      </ScenarioSection>
    </StyledScenarioCard>
  );
};
